import { useMemo, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import {
  STAKKO_ACCOUNT_URL,
  STAKKO_HELP_URL,
  STAKKO_PLAN_LABELS,
  STAKKO_PLAN_OFFERS,
  STAKKO_STRIPE_PORTAL_URL,
  STAKKO_SUPPORT_EMAIL,
  STAKKO_CREDIT_PACKS,
  STAKKO_CREDITS_ENABLED,
} from '../config';
import type { StakkoCheckoutKind } from '../config';
import { startStakkoCheckout } from '../billing';
import { STAKKO_APPS } from '../registry';
import { STAKKO_EVENTS, stakkoBus } from '../bus';
import { useStakko } from '../context/StakkoProvider';
import type { StakkoCreditPack, StakkoPanelView, StakkoTheme } from '../types';
import {
  ArrowUpRightIcon,
  BellIcon,
  CheckIcon,
  ChevronRightIcon,
  CoinsIcon,
  GiftIcon,
  GridIcon,
  HelpIcon,
  LogOutIcon,
  MoonIcon,
  SearchIcon,
  ShieldIcon,
  SparklesIcon,
  SunIcon,
  TrashIcon,
  UsersIcon,
  XIcon,
} from './icons';
import { StakkoAvatar, StakkoBadge, StakkoDivider, StakkoEmpty } from './primitives';
import { StakkoTodosPanel } from './StakkoTodosPanel';

export interface StakkoPanelContentProps {
  view: StakkoPanelView;
  onNavigate: (view: StakkoPanelView) => void;
  onClose: () => void;
  onBuyCredits?: (pack: StakkoCreditPack) => void;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('cs-CZ').format(value);
}

function formatDate(value: unknown): string {
  if (!value) return '';
  const seconds =
    typeof value === 'object' && value !== null && 'seconds' in value
      ? Number((value as { seconds: number }).seconds)
      : NaN;
  if (!Number.isFinite(seconds)) return '';
  const date = new Date(seconds * 1000);
  return date.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'short' });
}

const REASON_LABELS: Record<string, string> = {
  welcome: 'Uvítací bonus',
  referral: 'Pozvánka kamaráda',
  streak: 'Denní aktivita',
  shared_material: 'Sdílený materiál',
  ai_summary: 'AI shrnutí',
  ai_flashcards: 'Kartičky z materiálu',
  ai_explain: 'AI vysvětlení',
  export: 'Export',
  map_export: 'Export mapy',
  exam_pack: 'Zkouškový balíček',
};

function reasonLabel(reason: string): string {
  return REASON_LABELS[reason] ?? reason.replace(/_/g, ' ');
}

function translateAuthError(error: unknown): string {
  const code = (error as { code?: string }).code ?? '';
  switch (code) {
    case 'auth/invalid-email':
      return 'Neplatný e-mail.';
    case 'auth/user-not-found':
      return 'Účet s tímto e-mailem neexistuje.';
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Špatný e-mail nebo heslo.';
    case 'auth/email-already-in-use':
      return 'Účet s tímto e-mailem už existuje. Přihlas se.';
    case 'auth/weak-password':
      return 'Heslo musí mít alespoň 6 znaků.';
    case 'auth/too-many-requests':
      return 'Příliš mnoho pokusů. Zkus to za chvíli.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Přihlášení bylo zavřeno. Zkus to znovu.';
    case 'auth/network-request-failed':
      return 'Chyba sítě. Zkontroluj připojení.';
    default:
      return 'Něco se nepovedlo. Zkus to znovu.';
  }
}

/* ------------------------------------------------------------------ */
/* Account                                                             */
/* ------------------------------------------------------------------ */

function AccountPanel({ onNavigate }: StakkoPanelContentProps) {
  const {
    user,
    profile,
    entitlements,
    wallet,
    friends,
    incomingRequests,
    unreadNotifications,
    signOut,
  } = useStakko();

  if (!user) {
    return <AuthPanel onNavigate={onNavigate} />;
  }

  const name = profile?.displayName || user.displayName || user.email?.split('@')[0] || 'Student';
  const planLabel = STAKKO_PLAN_LABELS[entitlements.plan];

  return (
    <div className="stakko-panel-body">
      <div className="stakko-account-head">
        <StakkoAvatar name={name} photoURL={profile?.photoURL || user.photoURL} size={64} />
        <div className="stakko-account-meta">
          <strong>{name}</strong>
          <span>{user.email}</span>
          <StakkoBadge tone={entitlements.plan === 'free' ? 'muted' : 'brand'}>
            {planLabel}
          </StakkoBadge>
        </div>
      </div>

      <div className="stakko-credit-card">
        <div>
          <span className="stakko-credit-label">Kredity</span>
          <strong className="stakko-credit-value">{formatNumber(wallet.balance)}</strong>
        </div>
        <button
          type="button"
          className="stakko-btn stakko-btn-soft"
          onClick={() => onNavigate('credits')}
        >
          Detail
        </button>
      </div>

      <div className="stakko-quick-list">
        <button type="button" className="stakko-quick" onClick={() => onNavigate('plans')}>
          <span className="stakko-quick-icon" style={{ background: '#eef2ff', color: '#4f46e5' }}>
            <SparklesIcon size={18} />
          </span>
          <span>
            <strong>Tarif {planLabel}</strong>
            <em>
              {entitlements.plan === 'free'
                ? 'Odemkni Student+ nebo Student Pro'
                : 'Spravovat předplatné a fakturaci'}
            </em>
          </span>
          <ChevronRightIcon size={18} />
        </button>

        <button type="button" className="stakko-quick" onClick={() => onNavigate('apps')}>
          <span className="stakko-quick-icon" style={{ background: '#eef2ff', color: '#4f46e5' }}>
            <GridIcon size={18} />
          </span>
          <span>
            <strong>Aplikace</strong>
            <em>Přepni se do jiné části Stakko</em>
          </span>
          <ChevronRightIcon size={18} />
        </button>

        <button type="button" className="stakko-quick" onClick={() => onNavigate('friends')}>
          <span className="stakko-quick-icon" style={{ background: '#ecfdf5', color: '#059669' }}>
            <UsersIcon size={18} />
          </span>
          <span>
            <strong>Přátelé</strong>
            <em>
              {friends.length} {friends.length === 1 ? 'kamarád' : 'kamarádů'}
              {incomingRequests.length > 0 ? ` · ${incomingRequests.length} žádostí` : ''}
            </em>
          </span>
          <ChevronRightIcon size={18} />
        </button>

        <button type="button" className="stakko-quick" onClick={() => onNavigate('notifications')}>
          <span className="stakko-quick-icon" style={{ background: '#fff7ed', color: '#ea580c' }}>
            <BellIcon size={18} />
          </span>
          <span>
            <strong>Oznámení</strong>
            <em>{unreadNotifications > 0 ? `${unreadNotifications} nepřečtených` : 'Vše přečteno'}</em>
          </span>
          <ChevronRightIcon size={18} />
        </button>

        <button type="button" className="stakko-quick" onClick={() => onNavigate('settings')}>
          <span className="stakko-quick-icon" style={{ background: '#f1f5f9', color: '#475569' }}>
            <ShieldIcon size={18} />
          </span>
          <span>
            <strong>Nastavení účtu</strong>
            <em>Vzhled, soukromí a propojení</em>
          </span>
          <ChevronRightIcon size={18} />
        </button>
      </div>

      <div className="stakko-panel-actions">
        <a className="stakko-btn stakko-btn-soft" href={STAKKO_ACCOUNT_URL} target="_blank" rel="noreferrer">
          Spravovat účet <ArrowUpRightIcon size={16} />
        </a>
        <button type="button" className="stakko-btn stakko-btn-ghost" onClick={() => onNavigate('credits')}>
          <CoinsIcon size={16} /> Koupit kredity
        </button>
        <button
          type="button"
          className="stakko-btn stakko-btn-ghost stakko-btn-danger"
          onClick={() => void signOut()}
        >
          <LogOutIcon size={16} /> Odhlásit
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Auth                                                                */
/* ------------------------------------------------------------------ */

type AuthMode = 'signin' | 'signup' | 'reset';

function AuthPanel({ onNavigate }: { onNavigate: (view: StakkoPanelView) => void }) {
  const { externalAuth, signInWithGoogle, signInWithEmail, signUpWithEmail, sendPasswordReset } = useStakko();
  const [mode, setMode] = useState<AuthMode>('signin');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (externalAuth) {
    return (
      <div className="stakko-panel-body">
        <div className="stakko-auth-intro">
          <span className="stakko-auth-mark">V</span>
          <h3>Stakko účet</h3>
          <p>Přihlášení v této aplikaci zůstává na ní. Jakmile se přihlásíš, účet se propojí automaticky.</p>
        </div>
        <p className="stakko-inline-message">
          Použij přihlášení v horní části aplikace. Pak se ti tady objeví kredity, přátelé i oznámení.
        </p>
      </div>
    );
  }

  async function handleGoogle() {
    setBusy(true);
    setError(null);
    try {
      await signInWithGoogle();
      onNavigate('account');
    } catch (cause) {
      setError(translateAuthError(cause));
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'signin') await signInWithEmail(email, password);
      else if (mode === 'signup') await signUpWithEmail(email, password, name);
      else {
        await sendPasswordReset(email);
        setError('Poslali jsme ti e-mail pro obnovení hesla.');
        setBusy(false);
        return;
      }
      onNavigate('account');
    } catch (cause) {
      setError(translateAuthError(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stakko-panel-body">
      <div className="stakko-auth-intro">
        <span className="stakko-auth-mark">V</span>
        <h3>Jeden účet pro celý Stakko</h3>
        <p>Rozvrh, mapy, tréninky i akce. Přihlas se a měj vše propojené.</p>
      </div>

      <button
        type="button"
        className="stakko-btn stakko-btn-google"
        onClick={() => void handleGoogle()}
        disabled={busy}
      >
        <span className="stakko-google-g">G</span>
        Pokračovat přes Google
      </button>

      <StakkoDivider label="nebo e-mailem" />

      <form className="stakko-form" onSubmit={(event) => void handleSubmit(event)}>
        {mode === 'signup' ? (
          <label>
            <span>Jméno</span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Jak ti máme říkat?"
              autoComplete="name"
            />
          </label>
        ) : null}

        <label>
          <span>E-mail</span>
          <input
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="jmeno@skola.cz"
            autoComplete="email"
          />
        </label>

        {mode !== 'reset' ? (
          <label>
            <span>Heslo</span>
            <input
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Alespoň 6 znaků"
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            />
          </label>
        ) : null}

        {error ? <p className="stakko-form-error">{error}</p> : null}

        <button type="submit" className="stakko-btn stakko-btn-primary" disabled={busy}>
          {busy
            ? 'Pracuji…'
            : mode === 'signin'
              ? 'Přihlásit se'
              : mode === 'signup'
                ? 'Vytvořit účet'
                : 'Poslat odkaz pro obnovení'}
        </button>
      </form>

      <div className="stakko-auth-links">
        {mode === 'signin' ? (
          <>
            <button type="button" onClick={() => setMode('signup')}>
              Nemám účet
            </button>
            <button type="button" onClick={() => setMode('reset')}>
              Zapomenuté heslo
            </button>
          </>
        ) : (
          <button type="button" onClick={() => setMode('signin')}>
            Zpět na přihlášení
          </button>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Apps                                                                */
/* ------------------------------------------------------------------ */

function AppsPanel({ onNavigate }: StakkoPanelContentProps) {
  const { appId, hasApp } = useStakko();

  return (
    <div className="stakko-panel-body">
      <p className="stakko-panel-lead">
        Všechno, co používáš ve škole i mimo ni. Jeden účet, jedna peněženka.
      </p>

      <div className="stakko-app-grid">
        {STAKKO_APPS.map((app) => {
          const current = app.id === appId;
          const premium = hasApp(app.id);
          return (
            <a
              key={app.id}
              className={`stakko-app-card${current ? ' is-current' : ''}`}
              href={app.url}
              target="_blank"
              rel="noreferrer"
            >
              <span className="stakko-app-icon" style={{ background: app.color }}>
                {app.name.slice(0, 1)}
              </span>
              <span className="stakko-app-copy">
                <strong>{app.name}</strong>
                <em>{app.tagline}</em>
              </span>
              {current ? <StakkoBadge tone="muted">Tady</StakkoBadge> : null}
              {!current && premium ? <StakkoBadge tone="brand">V tarifu</StakkoBadge> : null}
              {!current && !premium ? <ArrowUpRightIcon size={16} /> : null}
            </a>
          );
        })}
      </div>

      <button type="button" className="stakko-btn stakko-btn-soft" onClick={() => onNavigate('credits')}>
        <SparklesIcon size={16} /> Co jde za kredity
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Credits                                                             */
/* ------------------------------------------------------------------ */

function CreditsPanel({ onNavigate, onBuyCredits }: StakkoPanelContentProps) {
  const { user, wallet, transactions } = useStakko();

  if (!STAKKO_CREDITS_ENABLED) {
    return (
      <div className="stakko-panel-body">
        <div className="stakko-wip">
          <span className="stakko-wip-icon">
            <CoinsIcon size={26} />
          </span>
          <h3>Kredity – pracujeme na tom</h3>
          <p>
            Kreditní systém právě ladíme. Brzy tady půjde dobít kredity a používat
            je na AI funkce napříč Stakkoem.
          </p>
          <p>Zatím můžeš využívat tarify Student+ a Student Pro.</p>
          <button
            type="button"
            className="stakko-btn stakko-btn-primary"
            onClick={() => onNavigate('plans')}
          >
            Zobrazit tarify
          </button>
        </div>
      </div>
    );
  }

  const currentUser = user;

  if (!currentUser) {
    return <AuthPanel onNavigate={onNavigate} />;
  }

  function buy(pack: StakkoCreditPack) {
    if (onBuyCredits) {
      onBuyCredits(pack);
      return;
    }
    const kind = pack.id as StakkoCheckoutKind;
    stakkoBus.emit(STAKKO_EVENTS.buyCredits, pack);
    startStakkoCheckout(kind, { uid: currentUser!.uid, email: currentUser!.email });
  }

  return (
    <div className="stakko-panel-body">
      <div className="stakko-wallet-hero">
        <span className="stakko-credit-label">Tvůj zůstatek</span>
        <strong>{formatNumber(wallet.balance)}</strong>
        <em>kreditů</em>
        <p>1 kredit = 1 AI akce nebo prémiový export.</p>
      </div>

      <div className="stakko-earn">
        <h4>Jak získat kredity</h4>
        <ul>
          <li>
            <GiftIcon size={16} /> Uvítací bonus <strong>+25</strong>
          </li>
          <li>
            <UsersIcon size={16} /> Pozvánka spolužáka <strong>+100</strong>
          </li>
          <li>
            <SparklesIcon size={16} /> Schválený sdílený materiál <strong>+50</strong>
          </li>
          <li>
            <CheckIcon size={16} /> Denní aktivita <strong>+5</strong>
          </li>
        </ul>
      </div>

      <div className="stakko-pack-grid">
        {STAKKO_CREDIT_PACKS.map((pack) => (
          <button
            key={pack.id}
            type="button"
            className={`stakko-pack${pack.label ? ' is-featured' : ''}`}
            onClick={() => buy(pack)}
          >
            {pack.label ? <span className="stakko-pack-label">{pack.label}</span> : null}
            <strong>{formatNumber(pack.credits)}</strong>
            <span>kreditů</span>
            <em>{formatNumber(pack.priceCzk)} Kč</em>
          </button>
        ))}
      </div>

      <button type="button" className="stakko-btn stakko-btn-soft" onClick={() => onNavigate('plans')}>
        <SparklesIcon size={16} /> Zobrazit tarify Student+ a Pro
      </button>

      <StakkoDivider label="Poslední pohyby" />

      {transactions.length === 0 ? (
        <StakkoEmpty
          icon={<CoinsIcon size={22} />}
          title="Zatím žádné pohyby"
          body="Jakmile použiješ AI nebo export, objeví se tady."
        />
      ) : (
        <ul className="stakko-tx-list">
          {transactions.map((tx) => (
            <li key={tx.id}>
              <span className={`stakko-tx-delta${tx.delta < 0 ? ' is-negative' : ''}`}>
                {tx.delta > 0 ? '+' : ''}
                {formatNumber(tx.delta)}
              </span>
              <span className="stakko-tx-copy">
                <strong>{reasonLabel(tx.reason)}</strong>
                <em>{formatDate(tx.createdAt)}</em>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Friends                                                             */
/* ------------------------------------------------------------------ */

function FriendsPanel({ onNavigate }: StakkoPanelContentProps) {
  const {
    user,
    friends,
    incomingRequests,
    findUserByEmail,
    sendFriendRequest,
    acceptFriendRequest,
    declineFriendRequest,
    removeFriend,
  } = useStakko();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [found, setFound] = useState<{ uid: string; displayName?: string | null; email?: string | null } | null>(null);

  if (!user) {
    return <AuthPanel onNavigate={onNavigate} />;
  }

  async function search(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    setFound(null);
    try {
      const result = await findUserByEmail(email);
      if (!result) {
        setMessage('Uživatel s tímto e-mailem tu ještě není. Ať se nejdřív přihlásí.');
        return;
      }
      if (result.uid === user?.uid) {
        setMessage('To jsi ty. Zkus e-mail kamaráda.');
        return;
      }
      setFound(result);
    } catch (cause) {
      console.warn(cause);
      setMessage('Hledání se nepovedlo. Zkus to znovu.');
    } finally {
      setBusy(false);
    }
  }

  async function add() {
    if (!found) return;
    setBusy(true);
    try {
      const status = await sendFriendRequest(found);
      setMessage(
        status === 'accepted'
          ? 'Hotovo – už jste přátelé.'
          : 'Žádost odeslána. Jakmile ji přijme, objeví se v seznamu.',
      );
      setFound(null);
      setEmail('');
    } catch (cause) {
      console.warn(cause);
      setMessage('Žádost se nepodařilo odeslat.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stakko-panel-body">
      <form className="stakko-search" onSubmit={(event) => void search(event)}>
        <SearchIcon size={18} />
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="E-mail kamaráda"
          aria-label="E-mail kamaráda"
        />
        <button type="submit" className="stakko-btn stakko-btn-primary" disabled={busy || !email}>
          Hledat
        </button>
      </form>

      {message ? <p className="stakko-inline-message">{message}</p> : null}

      {found ? (
        <div className="stakko-found">
          <StakkoAvatar name={found.displayName} size={36} />
          <span>
            <strong>{found.displayName || found.email}</strong>
            <em>{found.email}</em>
          </span>
          <button type="button" className="stakko-btn stakko-btn-primary" onClick={() => void add()}>
            Přidat
          </button>
        </div>
      ) : null}

      {incomingRequests.length > 0 ? (
        <>
          <StakkoDivider label="Žádosti" />
          <ul className="stakko-friend-list">
            {incomingRequests.map((friend) => (
              <li key={friend.id}>
                <StakkoAvatar name={friend.name} photoURL={friend.photoURL} size={34} />
                <span className="stakko-friend-copy">
                  <strong>{friend.name}</strong>
                  <em>{friend.email}</em>
                </span>
                <button
                  type="button"
                  className="stakko-icon-btn is-accept"
                  title="Přijmout"
                  onClick={() => void acceptFriendRequest(friend.id)}
                >
                  <CheckIcon size={16} />
                </button>
                <button
                  type="button"
                  className="stakko-icon-btn"
                  title="Odmítnout"
                  onClick={() => void declineFriendRequest(friend.id)}
                >
                  <XIcon size={16} />
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <StakkoDivider label={`Přátelé (${friends.length})`} />

      {friends.length === 0 ? (
        <StakkoEmpty
          icon={<UsersIcon size={22} />}
          title="Zatím nikdo"
          body="Přidej spolužáka a sdílejte rozvrh, mapy i materiály."
        />
      ) : (
        <ul className="stakko-friend-list">
          {friends.map((friend) => (
            <li key={friend.id}>
              <StakkoAvatar name={friend.name} photoURL={friend.photoURL} size={34} />
              <span className="stakko-friend-copy">
                <strong>{friend.name}</strong>
                <em>{friend.email || 'Stakko uživatel'}</em>
              </span>
              <button
                type="button"
                className="stakko-icon-btn stakko-icon-btn-danger"
                title="Odebrat"
                onClick={() => void removeFriend(friend.id)}
              >
                <TrashIcon size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Notifications                                                       */
/* ------------------------------------------------------------------ */

function NotificationsPanel({ onNavigate }: StakkoPanelContentProps) {
  const { user, notifications, unreadNotifications, markNotificationRead, markAllNotificationsRead } =
    useStakko();

  if (!user) {
    return <AuthPanel onNavigate={onNavigate} />;
  }

  return (
    <div className="stakko-panel-body">
      <div className="stakko-panel-row">
        <p className="stakko-panel-lead">
          {unreadNotifications > 0 ? `${unreadNotifications} nepřečtených` : 'Vše je přečtené'}
        </p>
        {unreadNotifications > 0 ? (
          <button type="button" className="stakko-link" onClick={() => void markAllNotificationsRead()}>
            Označit vše
          </button>
        ) : null}
      </div>

      {notifications.length === 0 ? (
        <StakkoEmpty
          icon={<BellIcon size={22} />}
          title="Žádná oznámení"
          body="Sem ti přijdou žádosti o přátelství, změny plánu i novinky z aplikací."
        />
      ) : (
        <ul className="stakko-notification-list">
          {notifications.map((item) => (
            <li key={item.id} className={item.readAt ? 'is-read' : ''}>
              <button
                type="button"
                onClick={() => {
                  if (!item.readAt) void markNotificationRead(item.id);
                  if (item.url) window.open(item.url, '_blank', 'noopener');
                }}
              >
                <span className="stakko-notification-dot" />
                <span className="stakko-notification-copy">
                  <strong>{item.title}</strong>
                  {item.body ? <em>{item.body}</em> : null}
                  <small>{formatDate(item.createdAt)}</small>
                </span>
                <ChevronRightIcon size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Plans                                                               */
/* ------------------------------------------------------------------ */

function PlansPanel({ onNavigate }: StakkoPanelContentProps) {
  const { user, entitlements } = useStakko();
  const [period, setPeriod] = useState<'monthly' | 'yearly'>('yearly');
  const currentUser = user;

  if (!currentUser) {
    return <AuthPanel onNavigate={onNavigate} />;
  }

  const active = entitlements.plan;

  return (
    <div className="stakko-panel-body">
      <p className="stakko-panel-lead">
        Jeden účet pro celý Stakko. Roční plán = dva měsíce zdarma.
      </p>

      <div className="stakko-segment">
        <button
          type="button"
          className={period === 'monthly' ? 'is-active' : ''}
          onClick={() => setPeriod('monthly')}
        >
          Měsíčně
        </button>
        <button
          type="button"
          className={period === 'yearly' ? 'is-active' : ''}
          onClick={() => setPeriod('yearly')}
        >
          Ročně · −2 měsíce
        </button>
      </div>

      {STAKKO_PLAN_OFFERS.map((offer) => {
        const isActive = active === offer.id;
        const price = period === 'monthly' ? offer.monthlyCzk : offer.yearlyCzk;
        const kind = period === 'monthly' ? offer.monthlyKind : offer.yearlyKind;
        return (
          <div key={offer.id} className={`stakko-plan-card${offer.id === 'pro' ? ' is-featured' : ''}`}>
            <div className="stakko-plan-head">
              <strong>{offer.name}</strong>
              {isActive ? <StakkoBadge tone="success">Aktivní</StakkoBadge> : null}
              {offer.id === 'pro' && !isActive ? <StakkoBadge tone="brand">Nejvíc</StakkoBadge> : null}
            </div>

            <div className="stakko-plan-price">
              <strong>{formatNumber(price)} Kč</strong>
              <span>/ {period === 'monthly' ? 'měsíc' : 'rok'}</span>
            </div>

            <ul className="stakko-plan-perks">
              {offer.perks.map((perk) => (
                <li key={perk}>
                  <CheckIcon size={15} /> {perk}
                </li>
              ))}
            </ul>

            <button
              type="button"
              className={`stakko-btn ${isActive ? 'stakko-btn-ghost' : 'stakko-btn-primary'}`}
              disabled={isActive}
              onClick={() =>
                startStakkoCheckout(kind, { uid: currentUser!.uid, email: currentUser!.email })
              }
            >
              {isActive ? 'Aktivní tarif' : 'Přejít na Stripe'}
            </button>
          </div>
        );
      })}

      {active !== 'free' ? (
        <div className="stakko-subscription-manage">
          <div>
            <strong>Tarif {STAKKO_PLAN_LABELS[active]} je aktivní</strong>
            <p>Předplatné můžeš kdykoli spravovat nebo zrušit.</p>
          </div>
          <a
            className="stakko-btn stakko-btn-ghost"
            href={
              STAKKO_STRIPE_PORTAL_URL
                ? STAKKO_STRIPE_PORTAL_URL
                : `mailto:${STAKKO_SUPPORT_EMAIL}?subject=${encodeURIComponent('Zrušení předplatného Stakko')}&body=${encodeURIComponent('Ahoj, chci zrušit předplatné Stakko. Můj e-mail: ')}`
            }
            target={STAKKO_STRIPE_PORTAL_URL ? '_blank' : undefined}
            rel={STAKKO_STRIPE_PORTAL_URL ? 'noreferrer' : undefined}
          >
            {STAKKO_STRIPE_PORTAL_URL
              ? 'Spravovat nebo zrušit členství'
              : 'Zrušit členství e-mailem'}
            <ArrowUpRightIcon size={15} />
          </a>
        </div>
      ) : null}

      <p className="stakko-version">
        Platba kartou přes Stripe. Přístup se aktivuje automaticky po zaplacení.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Settings                                                            */
/* ------------------------------------------------------------------ */

function SettingsPanel({ onNavigate }: StakkoPanelContentProps) {
  const { user, theme, setTheme, signOut, settingsExtension } = useStakko();
  const language = settingsExtension?.language;
  const themes = settingsExtension?.themes;
  const actions = settingsExtension?.actions ?? [];
  const links = settingsExtension?.links ?? [];

  return (
    <div className="stakko-panel-body">
      <h4 className="stakko-section-title">Vzhled panelu</h4>
      <div className="stakko-segment">
        {(
          [
            ['auto', 'Auto', <SunIcon key="auto" size={16} />],
            ['light', 'Světlý', <SunIcon key="light" size={16} />],
            ['dark', 'Tmavý', <MoonIcon key="dark" size={16} />],
          ] as [StakkoTheme, string, ReactNode][]
        ).map(([value, label, icon]) => (
          <button
            key={value}
            type="button"
            className={theme === value ? 'is-active' : ''}
            onClick={() => setTheme(value)}
          >
            {icon}
            {label}
          </button>
        ))}
      </div>

      {language ? (
        <>
          <h4 className="stakko-section-title">{language.label || 'Jazyk'}</h4>
          <div className="stakko-segment">
            {language.options.map((option) => (
              <button
                key={option.value}
                type="button"
                className={language.value === option.value ? 'is-active' : ''}
                onClick={() => language.onChange(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      ) : null}

      {themes ? (
        <>
          <h4 className="stakko-section-title">{themes.label || 'Motiv aplikace'}</h4>
          <div className="stakko-theme-grid">
            {themes.options.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`stakko-theme-card${themes.value === option.value ? ' is-active' : ''}`}
                style={option.swatch ? { background: option.swatch } : undefined}
                onClick={() => themes.onChange(option.value)}
                title={option.label}
                aria-label={option.label}
                aria-pressed={themes.value === option.value}
              >
                <span>{option.label}</span>
              </button>
            ))}
          </div>
        </>
      ) : null}

      {actions.length > 0 ? (
        <>
          <h4 className="stakko-section-title">Aplikace</h4>
          <div className="stakko-extension-actions">
            {actions.map((action) => (
              <button
                key={action.label}
                type="button"
                className="stakko-btn stakko-btn-ghost"
                onClick={action.onClick}
              >
                {action.label}
                {action.hint ? <em>{action.hint}</em> : null}
              </button>
            ))}
          </div>
        </>
      ) : null}

      <h4 className="stakko-section-title">Propojení</h4>
      <div className="stakko-link-list">
        <a href={STAKKO_ACCOUNT_URL} target="_blank" rel="noreferrer">
          <ShieldIcon size={18} /> Účet a soukromí <ArrowUpRightIcon size={15} />
        </a>
        <a href={STAKKO_HELP_URL} target="_blank" rel="noreferrer">
          <HelpIcon size={18} /> Nápověda <ArrowUpRightIcon size={15} />
        </a>
        {settingsExtension?.appName ? (
          <span className="stakko-version">Propojeno s aplikací {settingsExtension.appName}</span>
        ) : null}
      </div>

      {links.length > 0 ? (
        <>
          <h4 className="stakko-section-title">Odkazy aplikace</h4>
          <div className="stakko-link-list">
            {links.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target={link.href.startsWith('http') ? '_blank' : undefined}
                rel={link.href.startsWith('http') ? 'noreferrer' : undefined}
              >
                <ShieldIcon size={18} /> {link.label} <ArrowUpRightIcon size={15} />
              </a>
            ))}
          </div>
        </>
      ) : null}

      {settingsExtension?.about ? (
        <p className="stakko-extension-about">{settingsExtension.about}</p>
      ) : null}

      {user ? (
        <button
          type="button"
          className="stakko-btn stakko-btn-ghost stakko-btn-danger"
          onClick={() => void signOut()}
        >
          <LogOutIcon size={16} /> Odhlásit
        </button>
      ) : (
        <button type="button" className="stakko-btn stakko-btn-primary" onClick={() => onNavigate('auth')}>
          Přihlásit se
        </button>
      )}

      <p className="stakko-version">Stakko Core 0.1 · jeden účet pro všechny aplikace</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Router                                                              */
/* ------------------------------------------------------------------ */

export function StakkoPanelContent(props: StakkoPanelContentProps) {
  const content = useMemo(() => {
    switch (props.view) {
      case 'account':
        return <AccountPanel {...props} />;
      case 'auth':
        return <AuthPanel onNavigate={props.onNavigate} />;
      case 'apps':
        return <AppsPanel {...props} />;
      case 'credits':
        return <CreditsPanel {...props} />;
      case 'plans':
        return <PlansPanel {...props} />;
      case 'friends':
        return <FriendsPanel {...props} />;
      case 'notifications':
        return <NotificationsPanel {...props} />;
      case 'todos':
        return <StakkoTodosPanel onNavigate={props.onNavigate} />;
      case 'settings':
        return <SettingsPanel {...props} />;
      default:
        return <AccountPanel {...props} />;
    }
  }, [props]);

  return <>{content}</>;
}

export function panelTitle(view: StakkoPanelView): string {
  switch (view) {
    case 'account':
      return 'Účet';
    case 'auth':
      return 'Přihlášení';
    case 'apps':
      return 'Aplikace';
    case 'credits':
      return 'Kredity';
    case 'plans':
      return 'Tarify';
    case 'friends':
      return 'Přátelé';
    case 'notifications':
      return 'Oznámení';
    case 'todos':
      return 'Úkoly';
    case 'settings':
      return 'Nastavení';
    default:
      return 'Stakko';
  }
}
